import React from 'react';
import { ScaleIcon, SearchCheckIcon, HandshakeIcon, ReceiptIcon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { Section, SectionHeading, Eyebrow } from '../components/Section';
import { Reveal } from '../components/Reveal';
import { ClosingCTA } from '../components/ClosingCTA';
const principles = [{
  icon: ScaleIcon,
  title: 'Fee-only, so incentives stay aligned',
  body: 'We are paid a transparent advisory fee by the family and nothing by a manufacturer. No commissions, no revenue sharing, no targets on any desk in the firm.'
}, {
  icon: SearchCheckIcon,
  title: 'Nothing off the approved shelf',
  body: 'No adviser can recommend a product that has not cleared written due diligence at the Bengaluru desk. That rule protects you from an individual adviser’s enthusiasm as much as from a bad product.'
}, {
  icon: HandshakeIcon,
  title: 'One adviser, for years',
  body: 'Your adviser lives in your city and stays with your family. They carry the history of your decisions, which is most of the value — and in our two founding offices, that adviser is the founder.'
}, {
  icon: ReceiptIcon,
  title: 'Reported post-tax, in rupees',
  body: 'Returns are reported after tax and after our fee, across every entity. If a decision did not work, you will read it in the same statement.'
}];
const hub = ['Macro and asset allocation house view, reviewed quarterly', 'Model portfolios across five risk mandates', 'Written due diligence on every fund, PMS, AIF, bond and private-market opportunity', 'Retained panel of chartered accountants, lawyers and insurance analysts', 'Consolidated reporting and reconciliation'];
const spoke = ['Discovery of goals, liabilities and family dynamics', 'Implementation of the mandate your family chose', 'In-person reviews, at your home or office', 'Coordination with your CA and legal counsel', 'The difficult conversations, held face to face'];
export function Approach() {
  return <main data-id="element-242" data-mp="2pkfoi7w63029">
      <PageHeader eyebrow="Our approach" title="A central desk does the thinking. A local adviser does the knowing." lede="Good advice needs two things that rarely sit in the same place: institutional research depth, and genuine knowledge of a family. We separated them deliberately — the desk in Bengaluru where the firm was founded, and branches that start with Chennai and keep moving closer to where families actually live." data-id="element-243" data-mp="2po3g31i1xcec" />

      <Section data-id="element-244" data-mp="21xh6h5sefk28">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-8" data-id="element-245" data-mp="21xzcgu4v5cnc">
          <Reveal className="rounded-sm border border-line bg-paper p-8" data-id="element-246" data-mp="2focn549535gd">
            <Eyebrow data-id="element-247" data-mp="2ax4a7y8lp1yg">The hub</Eyebrow>
            <h2 className="mt-4 font-serif text-h3 text-charcoal" data-id="element-248" data-mp="2aw1lnwyr3blp">
              Central research &amp; due diligence, Bengaluru
            </h2>
            <ul className="mt-6 space-y-3" data-id="element-249" data-mp="1n5ffycc7y3ev">
              {hub.map(item => <li key={item} className="border-b border-line pb-3 text-[0.95rem] text-charcoal-600 last:border-0" data-id="element-250" data-mp="1j5oe9dl5m2vk">
                  {item}
                </li>)}
            </ul>
          </Reveal>

          <Reveal delay={0.06} className="rounded-sm border border-line bg-cream p-8" data-id="element-251" data-mp="1dut28svy89yv">
            <Eyebrow data-id="element-252" data-mp="z5n2n3t78hqg">The spokes</Eyebrow>
            <h2 className="mt-4 font-serif text-h3 text-charcoal" data-id="element-253" data-mp="142n93ftynmi3">
              Chennai today, with seven cities to follow
            </h2>
            <ul className="mt-6 space-y-3" data-id="element-254" data-mp="1rtsmlocfi2p8">
              {spoke.map(item => <li key={item} className="border-b border-line pb-3 text-[0.95rem] text-charcoal-600 last:border-0" data-id="element-255" data-mp="1wqj6kmrctifx">
                  {item}
                </li>)}
            </ul>
          </Reveal>
        </div>
      </Section>

      <Section tone="paper" data-id="element-256" data-mp="1mvhf3k9lw4a6">
        <SectionHeading eyebrow="Principles" title="Four rules we do not bend, even when a family asks us to." data-id="element-257" data-mp="6fem1geba5dz" />
        <div className="mt-12 grid gap-10 sm:grid-cols-2 sm:gap-x-12" data-id="element-258" data-mp="bfsswv46f9af">
          {principles.map((principle, index) => <Reveal key={principle.title} delay={index * 0.05} className="relative" data-id="element-259" data-mp="rngvdmk6jo2y" data-mpi={index}>
              <principle.icon className="h-5 w-5 text-gold-dark" aria-hidden="true" data-id="element-260" data-mp="mtclaufmazxn" />
              <h3 className="mt-4 font-serif text-h3 text-charcoal" data-id="element-261" data-mp="hwccwcvapl3d">{principle.title}</h3>
              <p className="mt-3 text-[0.95rem] leading-relaxed text-charcoal-600" data-id="element-262" data-mp="cxss4fsfufcl">
                {principle.body}
              </p>
            </Reveal>)}
        </div>
      </Section>

      <Section data-id="element-263" data-mp="15m6sh2701nrb">
        <Reveal className="mx-auto max-w-prose text-center" data-id="element-264" data-mp="15lokx9evzn43">
          <h2 className="font-serif text-h2 text-charcoal" data-id="element-265" data-mp="10liz49vasutx">
            “We would rather lose a mandate than sell a family something we would not own ourselves.”
          </h2>
          <p className="mt-6 text-sm text-charcoal-600" data-id="element-266" data-mp="3lc9sj1c87l93">Prashanth Sekhar, Founder</p>
        </Reveal>
      </Section>

      <ClosingCTA data-id="element-267" data-mp="3gcoquj5o8yso" />
    </main>;
}